/**
 * Documentation Center — /kcis/admin/documentation
 *
 * Admin-only read-only markdown viewer over `/app/docs/*.md`.
 * V1 features: category browsing, search, view, print, download,
 * version + last-updated metadata, source filename, doc-health widget.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

// Very small markdown renderer sufficient for the doc viewer.
// Uses a dependency-free path so we don't pull in an extra library.
function renderMarkdown(md) {
  if (!md) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  const out = [];
  let inCode = false;
  let inList = false;
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (!tableRows.length) return;
    let head = "";
    let body = "";
    for (let i = 0; i < tableRows.length; i++) {
      const cells = tableRows[i].split("|").filter((c, idx, arr) => !(idx === 0 && c.trim() === "") && !(idx === arr.length - 1 && c.trim() === ""));
      if (i === 1 && cells.every((c) => /^\s*:?-+:?\s*$/.test(c))) continue; // separator
      const tag = i === 0 ? "th" : "td";
      const row = cells.map((c) => `<${tag}>${inline(c.trim())}</${tag}>`).join("");
      if (i === 0) head += `<tr>${row}</tr>`;
      else body += `<tr>${row}</tr>`;
    }
    out.push(`<div class="doc-table-wrap"><table><thead>${head}</thead><tbody>${body}</tbody></table></div>`);
    tableRows = [];
    inTable = false;
  };

  const inline = (s) => {
    return escape(s)
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (raw.trim().startsWith("```")) {
      if (!inCode) { inCode = true; out.push("<pre><code>"); }
      else { inCode = false; out.push("</code></pre>"); }
      continue;
    }
    if (inCode) { out.push(escape(raw)); continue; }

    const isTableRow = /^\s*\|.*\|\s*$/.test(raw);
    if (isTableRow) {
      if (!inTable) { inTable = true; tableRows = []; }
      tableRows.push(raw.trim());
      continue;
    } else if (inTable) {
      flushTable();
    }

    if (/^#{1,6}\s/.test(raw)) {
      const m = raw.match(/^(#{1,6})\s+(.*)$/);
      const level = m[1].length;
      out.push(`<h${level}>${inline(m[2])}</h${level}>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(raw)) {
      if (!inList) { inList = true; out.push("<ul>"); }
      out.push(`<li>${inline(raw.replace(/^\s*[-*]\s+/, ""))}</li>`);
      continue;
    }
    if (inList) { inList = false; out.push("</ul>"); }
    if (raw.trim() === "") { out.push(""); continue; }
    if (/^\s*>/.test(raw)) { out.push(`<blockquote>${inline(raw.replace(/^\s*>\s?/, ""))}</blockquote>`); continue; }
    out.push(`<p>${inline(raw)}</p>`);
  }
  if (inList) out.push("</ul>");
  if (inTable) flushTable();
  if (inCode) out.push("</code></pre>");
  return out.join("\n");
}

export default function DocumentationCenter() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const activeSlug = params.get("doc") || "";
  const [manifest, setManifest] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [health, setHealth] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [errorRef, setErrorRef] = useState("");

  const loadManifest = useCallback(async () => {
    setError("");
    setErrorRef("");
    try {
      const { data } = await api.get("/kcis/admin/documentation/manifest", { timeout: 20000 });
      setManifest(data);
      const { data: h } = await api.get("/kcis/admin/documentation/health", { timeout: 20000 });
      setHealth(h);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) return; // handled by global interceptor
      const code = status ? `HTTP ${status}` : (e?.code || "network");
      const ref = `DOC-MFT-${Date.now()}`;
      setErrorRef(ref);
      setError(`Failed to load documentation manifest (${code}). Please retry, or share reference ${ref} with support.`);
    }
  }, []);

  useEffect(() => { loadManifest(); }, [loadManifest]);

  useEffect(() => {
    if (!activeSlug) { setDoc(null); return; }
    let alive = true;
    (async () => {
      setLoadingDoc(true);
      try {
        const { data } = await api.get(`/kcis/admin/documentation/${activeSlug}`);
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setDoc({ error: "Document not found." });
      } finally {
        if (alive) setLoadingDoc(false);
      }
    })();
    return () => { alive = false; };
  }, [activeSlug]);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults(null); return; }
    try {
      const { data } = await api.get(`/kcis/admin/documentation/search`, { params: { q } });
      setResults(data.results || []);
    } catch (e) {
      setResults([]);
    }
  }, []);

  const openDoc = (slug) => {
    setParams({ doc: slug });
    setResults(null); setSearch("");
    window.scrollTo({ top: 0 });
  };

  const download = async () => {
    if (!activeSlug) return;
    const token = localStorage.getItem("kcis_admin_token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backend}/api/kcis/admin/documentation/${activeSlug}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc?.source_filename || `${activeSlug}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <KcisAdminShell>
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <aside className="w-full lg:w-80 shrink-0 border-r p-4"
               style={{ borderColor: PAL.border, background: PAL.panel }}>
          <div className="mb-3">
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>Documentation</div>
            <div className="text-lg font-semibold" style={{ color: PAL.text }}>Center</div>
          </div>

          {/* Health widget */}
          {health && (
            <div className="rounded-lg p-3 mb-4 text-xs"
                 style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.muted }}
                 data-testid="docs-health-widget">
              <div className="flex items-center justify-between"><span>Total docs</span><span style={{ color: PAL.text }}>{health.total_documents}</span></div>
              <div className="flex items-center justify-between"><span>Healthy</span><span style={{ color: PAL.text }}>{health.healthy_documents}</span></div>
              <div className="flex items-center justify-between"><span>Pending</span><span style={{ color: health.pending_documents ? "#F59E0B" : PAL.text }}>{health.pending_documents}</span></div>
              <div className="flex items-center justify-between"><span>Categories</span><span style={{ color: PAL.text }}>{health.categories_count}</span></div>
            </div>
          )}

          {/* Search */}
          <form onSubmit={(e) => { e.preventDefault(); runSearch(search); }} className="mb-4">
            <input
              type="search"
              placeholder="Search documentation"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md px-3 py-2 text-sm"
              style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }}
              data-testid="docs-search-input"
            />
          </form>

          {results !== null ? (
            <div className="space-y-2" data-testid="docs-search-results">
              <div className="text-xs" style={{ color: PAL.muted }}>{results.length} match{results.length === 1 ? "" : "es"}</div>
              {results.length === 0 && <div className="text-sm" style={{ color: PAL.muted }}>No matches.</div>}
              {results.map((r) => (
                <button key={r.slug} onClick={() => openDoc(r.slug)}
                        className="w-full text-left rounded-md p-2 hover:bg-white/5"
                        data-testid={`docs-search-result-${r.slug}`}>
                  <div className="text-sm" style={{ color: PAL.text }}>{r.title}</div>
                  <div className="text-xs" style={{ color: PAL.muted }}>{r.category_label} · {r.match_count} match(es)</div>
                  {r.snippet && <div className="mt-1 text-xs opacity-75" style={{ color: PAL.muted }}>…{r.snippet}…</div>}
                </button>
              ))}
              <button onClick={() => { setResults(null); setSearch(""); }}
                      className="text-xs underline" style={{ color: PAL.accent }}>Clear search</button>
            </div>
          ) : (
            <div className="space-y-4">
              {manifest?.categories?.map((cat) => (
                <div key={cat.key}>
                  <div className="text-xs uppercase tracking-wider mb-2" style={{ color: PAL.muted }}>{cat.label}</div>
                  <div className="space-y-1">
                    {cat.documents.map((d) => (
                      <button
                        key={d.slug}
                        onClick={() => openDoc(d.slug)}
                        className="w-full text-left px-2 py-1.5 rounded-md text-sm"
                        data-testid={`docs-item-${d.slug}`}
                        style={{
                          background: activeSlug === d.slug ? PAL.accentSoft : "transparent",
                          color: activeSlug === d.slug ? PAL.text : PAL.muted,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{d.title}</span>
                          {!d.on_disk && <span className="text-[10px] px-1.5 py-0.5 rounded"
                                               style={{ background: "#7A4300", color: "#FDBA74" }}>Pending</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Main viewer */}
        <section className="flex-1 px-6 lg:px-10 py-8">
          {error && (
            <div className="rounded-2xl p-5 mb-6 border" style={{ borderColor: "#7A1F1F", background: "#241014" }} data-testid="docs-manifest-error">
              <div className="text-sm font-semibold" style={{ color: "#FDA4AF" }}>Documentation Center unavailable</div>
              <div className="mt-1 text-xs" style={{ color: PAL.muted }}>{error}</div>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={loadManifest}
                        className="rounded-full px-4 py-1.5 text-xs font-medium"
                        style={{ background: PAL.accent, color: "#0F0B14" }}
                        data-testid="docs-manifest-retry">
                  Retry
                </button>
                {errorRef && <span className="text-[10px]" style={{ color: PAL.muted }}>ref {errorRef}</span>}
              </div>
            </div>
          )}
          {!activeSlug && (
            <div className="max-w-2xl" data-testid="docs-welcome">
              <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>Welcome</div>
              <h1 className="text-3xl font-semibold mt-1">KCIS Documentation Center</h1>
              <p className="mt-3" style={{ color: PAL.muted }}>
                The official operational knowledge base for KCIS. Everything an
                administrator or trained employee needs to run the platform is
                available here — organized by category, searchable, printable,
                and downloadable.
              </p>
              <p className="mt-4" style={{ color: PAL.muted }}>
                Pick a document from the left to begin. Recent updates:
              </p>
              {health?.recent_changes?.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {health.recent_changes.slice(0, 5).map((r) => (
                    <li key={r.slug}>
                      <button onClick={() => openDoc(r.slug)}
                              className="underline text-sm"
                              style={{ color: PAL.accent }}
                              data-testid={`docs-recent-${r.slug}`}>
                        {r.title}
                      </button>
                      <span className="ml-2 text-xs" style={{ color: PAL.muted }}>
                        {r.category_label} · {r.version} · {r.last_updated}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeSlug && loadingDoc && <div style={{ color: PAL.muted }}>Loading…</div>}

          {activeSlug && doc && !doc.error && (
            <article className="max-w-4xl doc-viewer" data-testid="docs-viewer">
              <header className="mb-6 pb-4 border-b" style={{ borderColor: PAL.border }}>
                <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>{doc.category?.replace(/_/g, " ")}</div>
                <h1 className="text-3xl font-semibold mt-1" style={{ color: PAL.text }}>{doc.title}</h1>
                {doc.description && <p className="mt-2 text-sm" style={{ color: PAL.muted }}>{doc.description}</p>}
                <div className="mt-3 flex flex-wrap gap-3 text-xs" style={{ color: PAL.muted }}>
                  <span data-testid="docs-meta-version">Version: <strong style={{ color: PAL.text }}>{doc.version}</strong></span>
                  <span data-testid="docs-meta-updated">Last updated: <strong style={{ color: PAL.text }}>{doc.last_updated}</strong></span>
                  <span>File: <code style={{ color: PAL.text }}>{doc.source_filename}</code></span>
                  <span>Status: <strong style={{ color: PAL.text }}>Active</strong></span>
                  <span>{doc.word_count} words · {doc.line_count} lines</span>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => window.print()}
                          className="rounded-md px-3 py-1.5 text-xs border"
                          style={{ borderColor: PAL.border, color: PAL.text }}
                          data-testid="docs-btn-print">Print</button>
                  <button onClick={download}
                          className="rounded-md px-3 py-1.5 text-xs border"
                          style={{ borderColor: PAL.border, color: PAL.text }}
                          data-testid="docs-btn-download">Download .md</button>
                </div>
              </header>
              <div
                className="prose prose-invert max-w-none"
                data-testid="docs-body"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(doc.content_md) }}
              />
            </article>
          )}

          {activeSlug && doc?.error && (
            <div className="text-sm" style={{ color: "#F87171" }} data-testid="docs-error">
              {doc.error}
            </div>
          )}
        </section>
      </div>

      <style>{`
        .doc-viewer h1 { font-size: 1.9rem; font-weight: 600; margin: 1.5rem 0 0.5rem; color: ${PAL.text}; }
        .doc-viewer h2 { font-size: 1.4rem; font-weight: 600; margin: 1.5rem 0 0.5rem; color: ${PAL.text}; border-bottom: 1px solid ${PAL.border}; padding-bottom: 0.25rem; }
        .doc-viewer h3 { font-size: 1.15rem; font-weight: 600; margin: 1.25rem 0 0.4rem; color: ${PAL.text}; }
        .doc-viewer p { margin: 0.75rem 0; color: ${PAL.text}; line-height: 1.65; }
        .doc-viewer li { color: ${PAL.text}; line-height: 1.6; margin: 0.2rem 0; }
        .doc-viewer ul { margin: 0.5rem 0 0.75rem 1.25rem; list-style: disc; }
        .doc-viewer code { background: ${PAL.panel}; padding: 0.15rem 0.4rem; border-radius: 0.25rem; font-size: 0.9em; color: #FBBF24; }
        .doc-viewer pre { background: ${PAL.panel}; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; border: 1px solid ${PAL.border}; }
        .doc-viewer pre code { background: transparent; color: ${PAL.text}; padding: 0; }
        .doc-viewer blockquote { border-left: 3px solid ${PAL.accent}; padding-left: 1rem; margin: 1rem 0; color: ${PAL.muted}; }
        .doc-viewer a { color: ${PAL.accent}; text-decoration: underline; }
        .doc-viewer .doc-table-wrap { overflow-x: auto; margin: 1rem 0; }
        .doc-viewer table { border-collapse: collapse; width: 100%; font-size: 0.9em; }
        .doc-viewer th, .doc-viewer td { border: 1px solid ${PAL.border}; padding: 0.5rem 0.75rem; text-align: left; }
        .doc-viewer th { background: ${PAL.panel}; }
        @media print {
          aside { display: none !important; }
          section { padding: 0 !important; }
          body { background: white; color: black; }
          .doc-viewer h1, .doc-viewer h2, .doc-viewer h3, .doc-viewer p, .doc-viewer li { color: black !important; }
        }
      `}</style>
    </KcisAdminShell>
  );
}
